import React, { useState } from 'react';
import { collection, addDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../services/firebase';
import {
    Trophy, Calendar, Users, Globe, Clock, Shield, Upload,
    FileText, Link as LinkIcon, AlertCircle, Save, ChevronLeft, Image as ImageIcon
} from 'lucide-react';

const CreateTournament = ({ selectedClub, teams = [], onBack, onSuccess }) => {
    const [loading, setLoading] = useState(false);

    // Basic Details
    const [name, setName] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [ballType, setBallType] = useState('leather');

    // Organizer Details
    const [organizerName, setOrganizerName] = useState('');
    const [organizerLogo, setOrganizerLogo] = useState(null);
    const [contactPerson, setContactPerson] = useState('');
    const [contactNumber, setContactNumber] = useState('');
    const [tournamentLogo, setTournamentLogo] = useState(null);

    // URLs
    const [tournamentUrl, setTournamentUrl] = useState('');
    const [scheduleUrl, setScheduleUrl] = useState('');

    // Game Rules & Format
    const [maxSquadSize, setMaxSquadSize] = useState(11); // Playing XI size
    const [maxRosterSize, setMaxRosterSize] = useState(''); // Max players in squad
    const [allowSuperSubs, setAllowSuperSubs] = useState(false);
    const [numberOfOvers, setNumberOfOvers] = useState('');
    const [matchDurationHours, setMatchDurationHours] = useState('');
    const [matchDurationMinutes, setMatchDurationMinutes] = useState('');
    const [fieldingRestrictions, setFieldingRestrictions] = useState('');
    const [penaltyRules, setPenaltyRules] = useState('');

    // Time Settings
    const [reportingTimeOffset, setReportingTimeOffset] = useState(''); // e.g., "30 mins before"
    const [lateTimeOffset, setLateTimeOffset] = useState(''); // e.g., "15 mins after toss"

    // Teams
    const [selectedTeams, setSelectedTeams] = useState([]);

    const toggleTeamSelection = (teamId) => {
        setSelectedTeams(prev =>
            prev.includes(teamId)
                ? prev.filter(id => id !== teamId)
                : [...prev, teamId]
        );
    };

    // Image Upload Helper
    const handleImageUpload = async (file, path) => {
        if (!file) return null;
        try {
            const storageRef = ref(storage, `${path}/${Date.now()}_${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            return await getDownloadURL(snapshot.ref);
        } catch (error) {
            console.error("Error uploading image:", error);
            return null;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name || !startDate || !selectedClub) return;

        setLoading(true);
        try {
            console.log('🔵 Creating tournament:', name);

            // Upload Images if present
            const organizerLogoUrl = organizerLogo ? await handleImageUpload(organizerLogo, `clubs/${selectedClub.id}/tournaments/logos`) : null;
            const tournamentLogoUrl = tournamentLogo ? await handleImageUpload(tournamentLogo, `clubs/${selectedClub.id}/tournaments/logos`) : null;

            const tournament = {
                // Basic Info
                name,
                startDate,
                endDate: endDate || null,
                ballType,
                clubId: selectedClub.id,
                createdAt: new Date().toISOString(),

                // Organizer & Branding
                organizerName: organizerName || selectedClub.name,
                organizerLogo: organizerLogoUrl,
                tournamentLogo: tournamentLogoUrl,
                contactPerson,
                contactNumber,

                // Links
                tournamentUrl,
                scheduleUrl,

                // Format & Rules
                maxSquadSize: Number(maxSquadSize), // Playing size (11/8)
                maxRosterSize: maxRosterSize ? Number(maxRosterSize) : null, // Total squad size
                allowSuperSubs: allowSuperSubs,
                numberOfOvers: numberOfOvers ? Number(numberOfOvers) : null,
                matchDuration: (matchDurationHours || matchDurationMinutes) ? `${matchDurationHours || 0}h ${matchDurationMinutes || 0}m` : null,
                fieldingRestrictions,
                penaltyRules,

                // Timing
                reportingTimeOffset,
                lateTimeOffset,

                // Collections
                teams: selectedTeams,
                games: []
            };

            const docRef = await addDoc(collection(db, 'tournaments'), tournament);
            console.log('✅ Tournament created:', docRef.id);

            alert('Tournament created successfully!');
            onSuccess();
        } catch (error) {
            console.error('❌ Error creating tournament:', error);
            alert('Error creating tournament');
        } finally {
            setLoading(false);
        }
    };

    const FileInput = ({ label, onChange, previewFile }) => (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
            <div className="flex items-center gap-4">
                <label className="flex-1 cursor-pointer">
                    <div className="flex items-center justify-center w-full h-32 px-4 transition bg-white border-2 border-gray-300 border-dashed rounded-lg hover:bg-gray-50 hover:border-blue-400">
                        <div className="flex flex-col items-center space-y-2 text-gray-500">
                            {previewFile ? (
                                <span className="text-sm font-medium text-green-600">{previewFile.name}</span>
                            ) : (
                                <>
                                    <Upload size={24} />
                                    <span className="text-sm">Click to upload</span>
                                </>
                            )}
                        </div>
                    </div>
                    <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => onChange(e.target.files[0])}
                    />
                </label>
            </div>
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-sm overflow-hidden">
            {/* Header */}
            <div className="bg-gray-50 px-8 py-6 border-b flex justify-between items-center">
                <div>
                    <button onClick={onBack} className="text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2 transition-colors">
                        <ChevronLeft size={18} /> Back
                    </button>
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        <Trophy className="text-yellow-500" /> Create Tournament
                    </h2>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-8">

                {/* Section 1: Basic Details */}
                <section>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <FileText size={20} className="text-blue-500" /> Basic Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="md:col-span-2">
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Tournament Name *</label>
                            <input
                                required
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                placeholder="e.g. Summer Premier League 2024"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date *</label>
                            <input
                                required
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">End Date <span className="text-gray-400 font-normal">(Optional)</span></label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                </section>

                <hr className="border-gray-100" />

                {/* Section 2: Participating Teams */}
                <section>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Users size={20} className="text-indigo-500" /> Participating Teams
                    </h3>
                    <div className="bg-white border rounded-lg p-4">
                        {teams.length === 0 ? (
                            <p className="text-sm text-gray-500">No teams found for this club.</p>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                                {teams.map(team => (
                                    <label
                                        key={team.id}
                                        className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${selectedTeams.includes(team.id)
                                            ? 'bg-blue-50 border-blue-200'
                                            : 'hover:bg-gray-50 border-gray-200'
                                            }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedTeams.includes(team.id)}
                                            onChange={() => toggleTeamSelection(team.id)}
                                            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                        />
                                        <span className="ml-3 text-sm font-medium text-gray-900">
                                            {team.name}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </section>

                <hr className="border-gray-100" />

                {/* Section 3: Branding & Organizer */}
                <section>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <ImageIcon size={20} className="text-purple-500" /> Branding & Organizer
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Organizer Name</label>
                            <input
                                type="text"
                                value={organizerName}
                                onChange={(e) => setOrganizerName(e.target.value)}
                                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder={selectedClub?.name || "Organizer Name"}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Person</label>
                                <input
                                    type="text"
                                    value={contactPerson}
                                    onChange={(e) => setContactPerson(e.target.value)}
                                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="Name"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Contact Number</label>
                                <input
                                    type="tel"
                                    value={contactNumber}
                                    onChange={(e) => setContactNumber(e.target.value)}
                                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="Phone Number"
                                />
                            </div>
                        </div>

                        <FileInput
                            label="Tournament Logo"
                            onChange={setTournamentLogo}
                            previewFile={tournamentLogo}
                        />

                        <FileInput
                            label="Organizer Logo"
                            onChange={setOrganizerLogo}
                            previewFile={organizerLogo}
                        />
                    </div>
                </section>

                <hr className="border-gray-100" />

                {/* Section 4: Links */}
                <section>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Globe size={20} className="text-green-500" /> External Links
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Tournament Website URL</label>
                            <div className="relative">
                                <LinkIcon className="absolute left-3 top-3.5 text-gray-400" size={18} />
                                <input
                                    type="url"
                                    value={tournamentUrl}
                                    onChange={(e) => setTournamentUrl(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="https://..."
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Schedule/Fixtures URL</label>
                            <div className="relative">
                                <LinkIcon className="absolute left-3 top-3.5 text-gray-400" size={18} />
                                <input
                                    type="url"
                                    value={scheduleUrl}
                                    onChange={(e) => setScheduleUrl(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    placeholder="https://..."
                                />
                            </div>
                        </div>
                    </div>
                </section>

                <hr className="border-gray-100" />

                {/* Section 5: Game Format & Rules */}
                <section>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Shield size={20} className="text-red-500" /> Game Format & Rules
                    </h3>

                    {/* Ball Type Selection */}
                    <div className="mb-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Ball Type *</label>
                        <div className="grid grid-cols-2 gap-4">
                            <button
                                type="button"
                                onClick={() => {
                                    setBallType('leather');
                                    setMaxSquadSize(11);
                                }}
                                className={`p-4 rounded-lg border-2 text-center transition-all ${ballType === 'leather'
                                    ? 'border-red-500 bg-red-50 text-red-700'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                <span className="text-2xl mb-1 block">🔴</span>
                                <span className="font-semibold">Leather Ball</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setBallType('tennis');
                                    setMaxSquadSize(8);
                                }}
                                className={`p-4 rounded-lg border-2 text-center transition-all ${ballType === 'tennis'
                                    ? 'border-green-500 bg-green-50 text-green-700'
                                    : 'border-gray-200 hover:border-gray-300'
                                    }`}
                            >
                                <span className="text-2xl mb-1 block">🎾</span>
                                <span className="font-semibold">Tennis Ball</span>
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Playing Squad Size (XI)</label>
                            <div className="flex gap-2">
                                <select
                                    value={[11, 12, 8, 0].includes(maxSquadSize) ? maxSquadSize : 'custom'}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === 'custom') setMaxSquadSize('');
                                        else setMaxSquadSize(Number(val));
                                    }}
                                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value={11}>11 Players (Standard)</option>
                                    <option value={12}>12 Players (Super Sub)</option>
                                    <option value={8}>8 Players (Tennis)</option>
                                    <option value={0}>Unlimited</option>
                                    <option value="custom">Custom Size</option>
                                </select>
                                {![11, 12, 8, 0].includes(maxSquadSize) && (
                                    <input
                                        type="number"
                                        min="1"
                                        value={maxSquadSize}
                                        onChange={(e) => setMaxSquadSize(e.target.value === '' ? '' : Number(e.target.value))}
                                        className="w-24 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="Size"
                                        autoFocus
                                    />
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Team Roster Size Limit</label>
                            <input
                                type="number"
                                value={maxRosterSize}
                                onChange={(e) => setMaxRosterSize(e.target.value)}
                                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="Max players per team (e.g., 20)"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Overs</label>
                            <input
                                type="number"
                                value={numberOfOvers}
                                onChange={(e) => setNumberOfOvers(e.target.value)}
                                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g. 20"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Match Duration</label>
                            <div className="flex gap-2">
                                <div className="flex-1 flex items-center gap-2">
                                    <input
                                        type="number"
                                        min="0"
                                        value={matchDurationHours}
                                        onChange={(e) => setMatchDurationHours(e.target.value)}
                                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="0"
                                    />
                                    <span className="text-sm text-gray-600">hrs</span>
                                </div>
                                <div className="flex-1 flex items-center gap-2">
                                    <input
                                        type="number"
                                        min="0"
                                        max="59"
                                        value={matchDurationMinutes}
                                        onChange={(e) => setMatchDurationMinutes(e.target.value)}
                                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                        placeholder="0"
                                    />
                                    <span className="text-sm text-gray-600">mins</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mb-6 flex items-center">
                        <input
                            type="checkbox"
                            id="allowSuperSubs"
                            checked={allowSuperSubs}
                            onChange={(e) => setAllowSuperSubs(e.target.checked)}
                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label htmlFor="allowSuperSubs" className="ml-2 block text-sm text-gray-900 font-medium">
                            Allow Super Subs / Impact Players
                        </label>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Fielding Restrictions</label>
                            <textarea
                                rows={3}
                                value={fieldingRestrictions}
                                onChange={(e) => setFieldingRestrictions(e.target.value)}
                                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g. Powerplay rules, fielders outside circle..."
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Penalty Rules</label>
                            <textarea
                                rows={3}
                                value={penaltyRules}
                                onChange={(e) => setPenaltyRules(e.target.value)}
                                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g. Slow over rate penalties..."
                            />
                        </div>
                    </div>
                </section>


                <hr className="border-gray-100" />

                {/* Section 6: Timing & Logistics */}
                <section>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Clock size={20} className="text-orange-500" /> Timing & Logistics
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Reporting Time</label>
                            <input
                                type="text"
                                value={reportingTimeOffset}
                                onChange={(e) => setReportingTimeOffset(e.target.value)}
                                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g. 30 mins before start"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Late Arrival / Toss Forfeit</label>
                            <input
                                type="text"
                                value={lateTimeOffset}
                                onChange={(e) => setLateTimeOffset(e.target.value)}
                                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="e.g. 15 mins after toss"
                            />
                        </div>
                    </div>
                </section>

                {/* Footer Actions */}
                <div className="pt-6 border-t border-gray-100 flex gap-4">
                    <button
                        type="button"
                        onClick={onBack}
                        className="flex-1 px-6 py-4 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading || !name || !startDate}
                        className={`flex-1 px-6 py-4 rounded-lg text-white font-semibold flex items-center justify-center gap-2 transition-colors ${(loading || !name || !startDate)
                            ? 'bg-gray-300 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-200'
                            }`}
                    >
                        {loading ? (
                            <>
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                Creating...
                            </>
                        ) : (
                            <>
                                <Save size={20} /> Create Tournament
                            </>
                        )}
                    </button>
                </div>

            </form>
        </div>
    );
};

export default CreateTournament;
